/**
 * ComplianceTab Component
 * TASK-397: Post-Meeting Tabbed Redesign
 *
 * Document signatures + disclosure checklist (non-blocking).
 * Moved from separate ComplianceReview screen into a tab.
 * Exposes unsignedCount for badge rendering in parent.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Shield, CheckCircle2, AlertTriangle, X, FileText } from 'lucide-react';
import { ComplianceDocument, DisclosureItem } from '../../../types/meeting';
import {
  DocumentSignatureGrid,
  SignatureRequestModal,
  DisclosureChecklist,
} from '../ComplianceReview';
import FinsaDocumentationPanel from './FinsaDocumentationPanel';
import { postMeetingApi } from '../../../services/api/postMeetingApi';

// ============================================================================
// Props — uses canonical types from types/meeting.ts
// ============================================================================

interface MeetingComplianceTabProps {
  sessionId: string;
  documents: ComplianceDocument[];
  disclosures: DisclosureItem[];
  onDocumentsChange: (docs: ComplianceDocument[]) => void;
  onDisclosuresChange: (items: DisclosureItem[]) => void;
  onUnsignedCountChange?: (count: number) => void;
}

// ============================================================================
// Component
// ============================================================================

const MeetingComplianceTab: React.FC<MeetingComplianceTabProps> = ({
  sessionId,
  documents,
  disclosures,
  onDocumentsChange,
  onDisclosuresChange,
  onUnsignedCountChange,
}) => {
  const [selectedDocument, setSelectedDocument] = useState<ComplianceDocument | null>(null);
  const [signatureMethod, setSignatureMethod] = useState<'digital' | 'email' | 'paper'>('digital');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<ComplianceDocument | null>(null);

  const unsignedCount = useMemo(
    () => documents.filter((d) => d.signatureStatus !== 'signed').length,
    [documents],
  );

  useEffect(() => {
    onUnsignedCountChange?.(unsignedCount);
  }, [unsignedCount, onUnsignedCountChange]);

  const allCompliant = unsignedCount === 0 && disclosures.every((d) => !d.required || d.completed);

  const handleRequestSignature = useCallback(
    (doc: ComplianceDocument, method: 'digital' | 'email' | 'paper') => {
      setSelectedDocument(doc);
      setSignatureMethod(method);
      setShowSignatureModal(true);
    },
    [],
  );

  const handleSignatureComplete = useCallback(
    async (documentId: string, _signature: string, method: 'digital' | 'email' | 'paper') => {
      // Persist to backend
      try {
        await postMeetingApi.saveComplianceReview({
          session_id: sessionId,
          compliance_review: `Document ${documentId} signed via ${method}`,
          compliance_score: 100,
          documents_analyzed: 1,
          document_ids: [documentId],
        });
      } catch {
        // Non-fatal — update UI anyway, backend may not have the table yet
      }
      // Update local state
      const updated = documents.map((d) =>
        d.id === documentId
          ? { ...d, signatureStatus: 'signed' as const, signatureMethod: method, signedAt: new Date() }
          : d,
      );
      onDocumentsChange(updated);
      setShowSignatureModal(false);
      setSelectedDocument(null);
    },
    [sessionId, documents, onDocumentsChange],
  );

  const handleDisclosureToggle = useCallback(
    (disclosureId: string) => {
      const updated = disclosures.map((d) =>
        d.id === disclosureId ? { ...d, completed: !d.completed } : d,
      );
      onDisclosuresChange(updated);
    },
    [disclosures, onDisclosuresChange],
  );

  const handlePreviewDocument = useCallback((doc: ComplianceDocument) => {
    setPreviewDoc(doc);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Status banner */}
      {allCompliant ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-sm font-medium text-green-800">Fully Compliant</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm font-medium text-amber-800">
            {unsignedCount} document{unsignedCount !== 1 ? 's' : ''} requiring attention
          </p>
        </div>
      )}

      {/* TASK-704: the FINSA documentation this meeting actually produced. It was generated
          for every meeting-bound dictation and then discarded — read back from the record
          here, never regenerated, so what the RM sees is what is stored. */}
      <FinsaDocumentationPanel sessionId={sessionId} />

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document signatures */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-800">Document Signatures</h3>
            <span className="text-xs text-gray-500">
              {documents.filter((d) => d.signatureStatus === 'signed').length}/{documents.length} signed
            </span>
          </div>

          {documents.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">
              No documents required for this meeting type.
            </p>
          ) : (
            <DocumentSignatureGrid
              documents={documents}
              onRequestSignature={handleRequestSignature}
              onPreviewDocument={handlePreviewDocument}
            />
          )}
        </div>

        {/* Disclosure checklist */}
        <div>
          <DisclosureChecklist
            disclosures={disclosures}
            onDisclosureToggle={handleDisclosureToggle}
            readonly={false}
          />
        </div>
      </div>

      <p className="text-xs text-gray-400 italic">
        Compliance items are not blocking — you can finish the meeting with items pending.
      </p>

      {/* Signature modal */}
      {showSignatureModal && selectedDocument && (
        <SignatureRequestModal
          isOpen={showSignatureModal}
          onClose={() => {
            setShowSignatureModal(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
          method={signatureMethod}
          onSignatureComplete={(signature: string, method: 'digital' | 'email' | 'paper') =>
            handleSignatureComplete(selectedDocument.id, signature, method)
          }
        />
      )}

      {/* Document preview dialog */}
      {previewDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
            <button
              onClick={() => setPreviewDoc(null)}
              className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-blue-500" />
              <h3 className="text-lg font-semibold text-gray-900">{previewDoc.title}</h3>
            </div>
            {previewDoc.description && (
              <p className="text-sm text-gray-600 mb-4">{previewDoc.description}</p>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="font-medium capitalize">{previewDoc.documentType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Signature Status</span>
                <span className={`font-medium ${previewDoc.signatureStatus === 'signed' ? 'text-green-600' : 'text-amber-600'}`}>
                  {previewDoc.signatureStatus}
                </span>
              </div>
              {previewDoc.signedBy && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Signed By</span>
                  <span className="font-medium">{previewDoc.signedBy}</span>
                </div>
              )}
              {previewDoc.signedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Signed At</span>
                  <span className="font-medium">{new Date(previewDoc.signedAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingComplianceTab;
